"use client";

import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { Disturber } from "@/components/hero/Disturber";
import Image from "next/image";
import clsx from "clsx";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import { TempNote } from "@components/hero/TempNote";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "@store/selectors/auth.selectors";

export default function HeroContainer() {
  const { t } = useTranslation();
  const { currentSchema } = useStyleSchema();
  const heroBg = currentSchema?.hero?.background;
  const brandString = currentSchema?.brandString;
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Zeitbasierte Deaktivierung der TempNote
  const shouldShowTempNote = (): boolean => {
    const now = new Date();
    
    // Heutiges Datum als Startpunkt (ohne Zeit)
    const today = new Date(2025, 5, 10); // 10. Juni 2025 als Startdatum (Monat 5 = Juni!)
    
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);
    
    const tomorrowAfternoon = new Date(today);
    tomorrowAfternoon.setDate(tomorrowAfternoon.getDate() + 1);
    tomorrowAfternoon.setHours(15, 0, 0, 0);
    
    // Sonderbehandlung für Klier: Deaktivierung erst morgen Nachmittag (15:00)
    if (brandString === "KLIER") {
      return now < tomorrowAfternoon;
    }
    
    // Für alle anderen Marken: Deaktivierung heute Ende des Tages
    return now < endOfToday;
  };

  return (
    <div className={`hero-container min-h-[600px] md:min-h-[65vh] bg-[--color-blue-gray-25] flex flex-col md:flex-row items-center relative pt-60 md:pt-0 overflow-hidden`}>
      {/* Next.js Image als Hintergrund */}
      {heroBg && (
        <Image
          src={heroBg}
          alt={brandString}
          fill
          priority
          quality={85}
          sizes="100vw"
          style={{
            objectFit: "cover",
            objectPosition: "left center",
            zIndex: 0,
            transform: "translate3d(0, 0, 0)", // Hardware-Beschleunigung für bessere Performance
            willChange: "auto" // Optimierung für mobile Geräte
          }}
        />
      )}
      <div className="md:w-1/2 px-4 relative z-10"></div>
      <div className="w-full flex-1 md:flex-auto flex flex-col justify-center md:w-1/2 px-4 relative z-10">
        <Disturber />
        <h2 className="text-4xl font-exosoft md:text-5xl" style={{ color: currentSchema?.hero?.textColor }}>
          {t("hero.title")}
        </h2>
        <h3 className={clsx("text-4xl font-bold font-poppins md:text-5xl", {"mb-4": isMobile})}>
          <span style={{ color: currentSchema?.hero?.textColor }}>{t("hero.subtitle")}</span>{" "}
          <span className="inline" style={{ color: currentSchema?.hero?.textColor }}>{t("hero.subtitle2")}</span>
        </h3>
      </div>
      {!isAuthenticated && shouldShowTempNote() && <TempNote />}
    </div>
  );
}
